/**
 * AmalERP branding on prints — one small grey line on every client-facing
 * print:  "Powered by AmalERP · www.amalerp.com"
 *
 * It is fixed in code, not a Print Setting, so a company cannot change or
 * remove it. Only the platform owner (Super Admin → Currency & Tax) can switch
 * it off for one company — the company record's `hidePrintBranding`, which
 * company users cannot write (firestore.rules → companyProtectedKeys).
 *
 * Where the line sits:
 *  - A4 / A5 prints: in the bottom page margin of EVERY printed page, so it
 *    never covers anything and page breaks stay exactly as before. Browsers
 *    that cannot print in the page margin (old Chrome, Safari, Firefox) get it
 *    once, under the last line, instead. On screen it shows under the content.
 *  - Letterhead prints (no page margin): just above the letterhead footer.
 *  - Two copies on one sheet: at the bottom of each copy.
 *  - POS receipt / thermal printer / Z-report: the last line.
 *  - PDFs (Download PDF, WhatsApp): drawn at the bottom of every PDF page.
 *
 * Firebase cost: nothing — the text is in code and the switch is on the
 * company record that is already loaded.
 */

export const PRINT_BRAND_TEXT = 'Powered by AmalERP · www.amalerp.com';
// Thermal printers (ESC/POS) print plain ASCII only.
export const PRINT_BRAND_TEXT_ASCII = 'Powered by AmalERP - www.amalerp.com';

const FONT = 'Arial, Helvetica, sans-serif';
const COLOR = '#8a8f98';
const RGB = [138, 143, 152];

// The company whose prints are being made. Set by <PrintBrandHost /> (App.js)
// from the logged-in company, so print helpers that are not handed the company
// still follow its switch.
let currentCompany = null;
export const setPrintBrandCompany = (company) => { currentCompany = company || null; };

/** True only when Super Admin has switched the line off for this company. */
export const isPrintBrandHidden = (company) => {
  const c = company || currentCompany;
  return !!(c && c.hidePrintBranding === true);
};

/** Can this browser print in the page margin (CSS page-margin boxes)? */
export const marginBoxesSupported = () => {
  try { return typeof window !== 'undefined' && typeof window.CSSMarginRule !== 'undefined'; } catch (e) { return false; }
};

const escapeHtml = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// ── CSS pieces ───────────────────────────────────────────────────────────────

// Every printed page: the bottom page margin (does not move any content).
const PAGE_MARGIN_CSS = `@page{@bottom-center{content:"Powered by AmalERP \\B7  www.amalerp.com";`
  + `font-family:${FONT};font-size:7pt;font-weight:normal;font-style:normal;color:${COLOR};`
  + 'vertical-align:top;padding-top:1.2mm}}';

// The line under the content: always on screen; on paper only when the browser
// cannot print in the page margin. Guarded so a print's own styles can't hide
// or restyle it.
const endLineCss = (inPrint) => `.amal-print-brand{display:block !important;clear:both !important;float:none !important;`
  + 'position:static !important;width:auto !important;height:auto !important;margin:6mm 0 0 !important;padding:0 !important;'
  + 'border:0 !important;background:none !important;visibility:visible !important;opacity:1 !important;text-align:center !important;'
  + `font-family:${FONT} !important;font-size:7pt !important;font-weight:normal !important;font-style:normal !important;`
  + `line-height:1.3 !important;letter-spacing:0 !important;text-transform:none !important;color:${COLOR} !important;`
  + 'break-inside:avoid;page-break-inside:avoid}'
  + (inPrint ? '' : '@media print{.amal-print-brand{display:none !important}}');

/** CSS for a normal (A4 / A5, with page margins) print. */
export const printBrandCss = () => {
  const mb = marginBoxesSupported();
  return (mb ? PAGE_MARGIN_CSS : '') + endLineCss(!mb);
};

export const printBrandEndHtml = () => `<div class="amal-print-brand">${escapeHtml(PRINT_BRAND_TEXT)}</div>`;

/**
 * Letterhead prints have no page margin: the line sits just above the
 * letterhead footer picture (or 6 mm from the bottom when there is none), and
 * the content gets that much more room at its end so the last line is clear.
 */
export const printBrandLetterheadCss = (lh) => {
  const hasFooter = !!(lh && lh.footerUrl);
  const footerMm = hasFooter ? Number(lh.footerHeightMm) || 0 : 0;
  return `.amal-print-brand-lh{position:fixed;left:0;right:0;bottom:${hasFooter ? `${footerMm + 1.5}mm` : '6mm'};`
    + `margin:0;padding:0;text-align:center;font-family:${FONT};font-size:7pt;font-weight:normal;line-height:1.3;`
    + `color:${COLOR};z-index:3;pointer-events:none;background:none}`
    + (hasFooter ? `body{padding-bottom:${footerMm + 6}mm !important}` : '');
};
export const printBrandLetterheadHtml = () => `<div class="amal-print-brand-lh">${escapeHtml(PRINT_BRAND_TEXT)}</div>`;

/** Two copies on one sheet: the line at the bottom of each copy. */
export const printBrandHalvesCss = () => '.copy-half{position:relative}'
  + '.amal-print-brand-half{position:absolute;left:0;right:0;bottom:0;height:3.6mm;line-height:3.6mm;margin:0;padding:0;'
  + `background:#fff;text-align:center;font-family:${FONT};font-size:6.5pt;font-weight:normal;color:${COLOR};z-index:2}`;
export const printBrandHalfHtml = () => `<div class="amal-print-brand-half">${escapeHtml(PRINT_BRAND_TEXT)}</div>`;

// ── Adding it to a print ─────────────────────────────────────────────────────

/** Add the line to a complete print page (HTML text). */
export function withPrintBrand(html, company) {
  if (!html || isPrintBrandHidden(company)) return html;
  const style = `<style data-amal-print-brand>${printBrandCss()}</style>`;
  let out = String(html);
  const h = out.lastIndexOf('</head>');
  out = h >= 0 ? out.slice(0, h) + style + out.slice(h) : style + out;
  const b = out.lastIndexOf('</body>');
  out = b >= 0 ? out.slice(0, b) + printBrandEndHtml() + out.slice(b) : out + printBrandEndHtml();
  return out;
}

/**
 * Add the line to a print window opened with window.open + document.write.
 * Call it after the last write and before document.close(). Never throws —
 * branding must never stop a print.
 */
export function addPrintBrand(win, company) {
  try {
    if (!win || !win.document || isPrintBrandHidden(company)) return;
    const doc = win.document;
    if (doc.querySelector('.amal-print-brand')) return;
    const style = doc.createElement('style');
    style.setAttribute('data-amal-print-brand', '');
    style.textContent = printBrandCss();
    (doc.head || doc.documentElement).appendChild(style);
    const line = doc.createElement('div');
    line.className = 'amal-print-brand';
    line.textContent = PRINT_BRAND_TEXT;
    (doc.body || doc.documentElement).appendChild(line);
  } catch (e) { /* ignore */ }
}

/** Draw the line on the current jsPDF page, centred, baseline at yMm. */
export function drawPdfBrandLine(pdf, pageWidthMm, yMm) {
  try {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7);
    pdf.setTextColor(RGB[0], RGB[1], RGB[2]);
    pdf.text(PRINT_BRAND_TEXT, pageWidthMm / 2, yMm, { align: 'center' });
  } catch (e) { /* ignore */ }
}

/** Thermal (ESC/POS) lines for a receipt `chars` wide. */
export const printBrandThermalLines = (chars) => (chars >= PRINT_BRAND_TEXT_ASCII.length
  ? [PRINT_BRAND_TEXT_ASCII]
  : ['Powered by AmalERP', 'www.amalerp.com']);
